import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  SxProps,
  Switch,
  Theme,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LanRoundedIcon from "@mui/icons-material/LanRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { useCallback, useEffect, useRef, useState } from "react";
import Popup from "../../../components/common/model/Popup";
import CustomButton from "../../../components/common/button/CustomButton";
import { useSelector } from "react-redux";

interface ManageImportLeadsProps {
  open: boolean;
  onHide: (options?: { shouldRefresh?: boolean }) => void;
}

const formatLabel = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isTerminalRunStatus = (value: unknown) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "completed" || normalized === "failed" || normalized === "error";
};

const MAIL_ACTIVITY_MESSAGE = /(new\s+mail|new\s+message|mail\s+detected|message\s+detected|inbox)/i;
const IMPORT_API_BASE = (
  process.env.REACT_APP_IMPORT_API_BASE_URL
  || process.env.REACT_APP_BASE_URL
  || "https://dev-api.managemymoves.co.uk/v1"
).replace(/\/$/, "");
const IMPORT_WS_URL = process.env.REACT_APP_IMPORT_WS_URL
  || "wss://ibmscrm22d.execute-api.ap-south-1.amazonaws.com/v1";
const STATUS_POLL_EVERY_MS = 5000;
const LIVE_INBOX_POLL_EVERY_MS = 2000;
const RUN_ID_STORAGE_KEY = "importRunId";
const LAST_SEEN_INBOX_MESSAGE_ID_STORAGE_KEY = "importLastSeenInboxMessageId";
const SUMMARY_METRIC_ORDER = [
  "received",
  "scanned",
  "parsedCandidates",
  "added",
  "duplicates",
  "rejected",
  "errors",
  "totalDetected",
  "processed",
] as const;
const REASON_PRIMARY_KEYS = [
  "reason",
  "message",
  "duplicateReason",
  "rejectedReason",
  "code",
  "title",
] as const;

type ImportSummary = Record<string, unknown>;
type ReasonListItem = {
  count?: number | null;
  key: string;
  primary: string;
  secondary?: string;
};

type InboxStatusPayload = {
  type?: string;
  status?: string;
  message?: string;
  inboxFolder?: string;
  messageCount?: number | string | null;
  latestMessageId?: number | string | null;
  transport?: string;
  connectionId?: string;
  requestId?: string;
  runId?: string;
  summary?: ImportSummary;
  rejectedReasonCounts?: Record<string, unknown>;
};

type FeedbackState = {
  severity: "success" | "info" | "warning" | "error";
  message: string;
} | null;

const normalizeCount = (value: unknown) => {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeMessageId = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const pickFirstPopulated = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const formatDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatDisplayValue(item)).join(", ");
  }

  if (isRecord(value)) {
    return JSON.stringify(value);
  }

  return String(value);
};

const sortMetricEntries = ([leftKey]: [string, unknown], [rightKey]: [string, unknown]) => {
  const leftIndex = SUMMARY_METRIC_ORDER.indexOf(leftKey as (typeof SUMMARY_METRIC_ORDER)[number]);
  const rightIndex = SUMMARY_METRIC_ORDER.indexOf(rightKey as (typeof SUMMARY_METRIC_ORDER)[number]);

  if (leftIndex === -1 && rightIndex === -1) {
    return leftKey.localeCompare(rightKey);
  }

  if (leftIndex === -1) {
    return 1;
  }

  if (rightIndex === -1) {
    return -1;
  }

  return leftIndex - rightIndex;
};

const toSortedEntries = (value: unknown) =>
  isRecord(value)
    ? Object.entries(value).sort(
      ([leftKey], [rightKey]) => String(leftKey).localeCompare(String(rightKey)),
    )
    : [];

const normalizeReasonList = (value: unknown): ReasonListItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item, index) => {
    if (!isRecord(item)) {
      const label = formatDisplayValue(item);
      return {
        key: `${label}-${index}`,
        primary: label,
      };
    }

    const primary = pickFirstPopulated(
      ...REASON_PRIMARY_KEYS.map((key) => {
        const entry = item[key];
        return typeof entry === "string" ? entry.trim() : entry;
      }),
    );

    const secondary = Object.entries(item)
      .filter(
        ([key, entryValue]) =>
          !REASON_PRIMARY_KEYS.includes(key as (typeof REASON_PRIMARY_KEYS)[number])
          && key !== "count"
          && entryValue !== null
          && entryValue !== undefined
          && entryValue !== "",
      )
      .slice(0, 3)
      .map(([key, entryValue]) => `${formatLabel(key)}: ${formatDisplayValue(entryValue)}`)
      .join(" • ");

    const normalizedCount = Number(item.count);

    return {
      count: Number.isFinite(normalizedCount) ? normalizedCount : null,
      key: `${String(primary ?? "reason")}-${index}`,
      primary: formatDisplayValue(primary ?? `Reason ${index + 1}`),
      secondary: secondary || undefined,
    };
  });
};

const getRunStatusMeta = (value: string) => {
  const normalized = value.trim().toLowerCase();

  if (normalized === "completed") {
    return {
      accent: "#1c8b6f",
      chipColor: "success" as const,
      chipIcon: <CheckCircleRoundedIcon fontSize="small" />,
      soft: "#eaf8f4",
    };
  }

  if (normalized === "failed" || normalized === "error") {
    return {
      accent: "#d95c5c",
      chipColor: "error" as const,
      chipIcon: <ErrorOutlineRoundedIcon fontSize="small" />,
      soft: "#fff1f1",
    };
  }

  if (
    normalized === "ok"
    || normalized === "running"
    || normalized === "accepted"
    || normalized === "processing"
  ) {
    return {
      accent: "#1f6fa8",
      chipColor: "info" as const,
      chipIcon: <SyncRoundedIcon fontSize="small" />,
      soft: "#edf6fd",
    };
  }

  return {
    accent: "#60758b",
    chipColor: "default" as const,
    chipIcon: <InfoOutlinedIcon fontSize="small" />,
    soft: "#f5f8fb",
  };
};

const getMetricCardSx = (key: string, value: unknown): SxProps<Theme> => {
  const count = normalizeCount(value);

  if (key === "added") {
    return {
      ...metricCardBaseSx,
      borderColor: "#cae9dc",
      background: "linear-gradient(180deg, #f8fffb 0%, #edf9f3 100%)",
      boxShadow: "0 10px 22px rgba(28, 139, 111, 0.08)",
    };
  }

  if (key === "duplicates" && count > 0) {
    return {
      ...metricCardBaseSx,
      borderColor: "#f4ddb4",
      background: "linear-gradient(180deg, #fffaf0 0%, #fff4df 100%)",
      boxShadow: "0 10px 22px rgba(207, 138, 30, 0.08)",
    };
  }

  if (key === "rejected" && count > 0) {
    return {
      ...metricCardBaseSx,
      borderColor: "#f0ceb4",
      background: "linear-gradient(180deg, #fff8f2 0%, #ffefdf 100%)",
      boxShadow: "0 10px 22px rgba(217, 122, 51, 0.08)",
    };
  }

  if (key === "errors" && count > 0) {
    return {
      ...metricCardBaseSx,
      borderColor: "#efc1c1",
      background: "linear-gradient(180deg, #fff7f7 0%, #ffe9e9 100%)",
      boxShadow: "0 10px 22px rgba(217, 92, 92, 0.08)",
    };
  }

  if (key === "processed" || key === "totalDetected") {
    return {
      ...metricCardBaseSx,
      borderColor: "#cce1f1",
      background: "linear-gradient(180deg, #f7fbfe 0%, #eef6fc 100%)",
      boxShadow: "0 10px 22px rgba(31, 111, 168, 0.06)",
    };
  }

  return metricCardBaseSx;
};

const decodeJwtPayload = (token: string) => {
  try {
    const [, payload = ""] = token.split(".");
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      "=",
    );

    return JSON.parse(atob(paddedPayload));
  } catch {
    return null;
  }
};

const resolveTenantId = (userProfile: any, user: any, authToken: string) => {
  const tokenPayload = decodeJwtPayload(authToken);
  const candidates = [
    userProfile?.tenant_id,
    userProfile?.tenantId,
    userProfile?.client?.tenant_id,
    userProfile?.client?.tenantId,
    userProfile?.company?.tenant_id,
    userProfile?.company?.tenantId,
    user?.tenant_id,
    user?.tenantId,
    user?.user?.tenant_id,
    user?.user?.tenantId,
    tokenPayload?.tenantId,
    tokenPayload?.tenant_id,
    tokenPayload?.["custom:tenantId"],
  ];

  const tenantValue = candidates.find((candidate) => String(candidate ?? "").trim());
  return String(tenantValue ?? "").trim();
};

const ManageImportLeads: React.FC<ManageImportLeadsProps> = (props) => {
  const { userProfile, user } = useSelector((state: any) => state.user);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMode, setProgressMode] = useState<"determinate" | "indeterminate">("determinate");
  const [runId, setRunId] = useState<string>(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return localStorage.getItem(RUN_ID_STORAGE_KEY) || "";
  });
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [importResponse, setImportResponse] = useState<any>(null);
  const [statusSummary, setStatusSummary] = useState<any>(null);
  const [hasNewMail, setHasNewMail] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [liveInboxWatch, setLiveInboxWatch] = useState(false);
  const [isCheckingInbox, setIsCheckingInbox] = useState(false);
  const [inboxMessageCount, setInboxMessageCount] = useState<number | null>(null);
  const [, setLatestInboxMessageId] = useState<number | null>(null);
  const [, setLastInboxCheckAt] = useState<number | null>(null);
  const [inboxFolder, setInboxFolder] = useState("INBOX");
  const [inboxWatchTransport, setInboxWatchTransport] = useState<"http" | "websocket">("http");
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const previousDetectedRef = useRef<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const inboxRequestInFlightRef = useRef(false);
  const lastSeenInboxMessageIdRef = useRef<number | null>(null);
  const latestInboxMessageIdRef = useRef<number | null>(null);

  const activeRunId = runId.trim();
  const authToken = String(user?.accessToken ?? "").trim();
  const tenantId = resolveTenantId(userProfile, user, authToken);
  const canUseWs = Boolean(IMPORT_WS_URL.trim());

  const setFeedbackMessage = useCallback((severity: NonNullable<FeedbackState>["severity"], message: string) => {
    setFeedback({ severity, message });
  }, []);

  const markInboxAsSeen = useCallback((messageId: number | null) => {
    if (typeof window === "undefined" || messageId === null) {
      return;
    }

    lastSeenInboxMessageIdRef.current = messageId;
    localStorage.setItem(LAST_SEEN_INBOX_MESSAGE_ID_STORAGE_KEY, String(messageId));
    setHasNewMail(false);
  }, []);

  const applyInboxStatus = useCallback((payload: InboxStatusPayload) => {
    const nextMessageCount = normalizeCount(payload.messageCount);
    const nextMessageId = normalizeMessageId(payload.latestMessageId);

    setInboxFolder(String(payload.inboxFolder ?? "INBOX"));
    setInboxMessageCount(nextMessageCount);
    setLatestInboxMessageId(nextMessageId);
    latestInboxMessageIdRef.current = nextMessageId;
    setLastInboxCheckAt(Date.now());

    if (
      nextMessageId !== null
      && lastSeenInboxMessageIdRef.current !== null
      && nextMessageId > lastSeenInboxMessageIdRef.current
    ) {
      setHasNewMail(true);
    }
  }, []);

  const applyStatusData = useCallback((data: any) => {
    const nextRunId = String(data?.runId || "").trim();
    if (nextRunId) {
      setRunId(nextRunId);
      localStorage.setItem(RUN_ID_STORAGE_KEY, nextRunId);
    }

    const nextDetected = Number(
      data?.counters?.totalDetected
      ?? data?.counters?.received
      ?? data?.summary?.received
      ?? 0,
    );

    const lastMessage = String(data?.lastMessage || "");
    const detectedNewMail =
      (previousDetectedRef.current !== null && nextDetected > previousDetectedRef.current)
      || MAIL_ACTIVITY_MESSAGE.test(lastMessage);

    if (Number.isFinite(nextDetected)) {
      previousDetectedRef.current = nextDetected;
    }

    if (detectedNewMail) {
      setHasNewMail(true);
    }

    setStatusSummary(data);
    setImporting(!isTerminalRunStatus(data?.status));
    const totalDetected = Number(data?.counters?.totalDetected ?? 0);
    const processed = Number(data?.counters?.processed ?? 0);
    const isCompleted = String(data?.status ?? "").toLowerCase() === "completed";

    if (isCompleted) {
      setProgressMode("determinate");
      setProgress(100);
    } else if (totalDetected > 0 && processed >= 0) {
      setProgressMode("determinate");
      setProgress(Math.min(100, Math.round((processed / totalDetected) * 100)));
    } else {
      setProgressMode("indeterminate");
      setProgress(0);
    }

    if (isCompleted) {
      markInboxAsSeen(latestInboxMessageIdRef.current);
      setImporting(false);
    }
  }, [markInboxAsSeen]);

  const disconnectSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
  }, []);

  const handleWebSocketTransportFailure = useCallback((message: string) => {
    inboxRequestInFlightRef.current = false;
    setIsCheckingInbox(false);
    setInboxWatchTransport("http");
    setWsConnected(false);
    setFeedbackMessage("warning", `${message} Falling back to HTTP inbox checks.`);
  }, [setFeedbackMessage]);

  const connectSocket = useCallback(() => {
    if (!canUseWs) {
      setFeedbackMessage("error", "Missing REACT_APP_IMPORT_WS_URL configuration.");
      return;
    }

    disconnectSocket();

    const socket = new WebSocket(IMPORT_WS_URL);
    wsRef.current = socket;

    socket.onopen = () => {
      setWsConnected(true);
      setFeedbackMessage("success", `WebSocket connected: ${IMPORT_WS_URL}`);
    };

    socket.onclose = () => {
      wsRef.current = null;
      setWsConnected(false);
    };

    socket.onerror = () => {
      handleWebSocketTransportFailure("WebSocket error. Check endpoint or network.");
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(String(event.data)) as InboxStatusPayload;

        if (payload?.type === "inbox-status") {
          inboxRequestInFlightRef.current = false;
          setIsCheckingInbox(false);

          if (payload.status === "error") {
            setFeedbackMessage("error", payload.message || "Inbox watch request failed.");
            return;
          }

          applyInboxStatus(payload);
          setInboxWatchTransport("websocket");
          setFeedbackMessage("success", "Inbox status updated over WebSocket.");
          return;
        }

        if (
          payload?.message === "Forbidden"
          && typeof payload.connectionId === "string"
          && typeof payload.requestId === "string"
        ) {
          handleWebSocketTransportFailure("WebSocket endpoint rejected the message.");
          return;
        }
      } catch {
        setFeedbackMessage("info", `WebSocket message: ${String(event.data)}`);
      }
    };
  }, [applyInboxStatus, canUseWs, disconnectSocket, handleWebSocketTransportFailure, setFeedbackMessage]);

  const sendInboxStatusOverSocket = useCallback((options?: { silent?: boolean }) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return false;
    }

    if (!authToken) {
      if (!options?.silent) {
        setFeedbackMessage("error", "Auth token is required.");
      }
      return true;
    }

    if (!tenantId) {
      if (!options?.silent) {
        setFeedbackMessage("error", "tenant_id is required for inbox checks.");
      }
      return true;
    }

    wsRef.current.send(JSON.stringify({
      action: "inbox-status",
      authorization: authToken,
      tenant_id: tenantId,
    }));

    inboxRequestInFlightRef.current = true;
    setInboxWatchTransport("websocket");
    if (!options?.silent) {
      setIsCheckingInbox(true);
    }
    return true;
  }, [authToken, setFeedbackMessage, tenantId]);

  const fetchStatus = useCallback(async (statusRunId?: string) => {
    const id = (statusRunId || activeRunId).trim();
    if (!id) {
      return false;
    }

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };
      const res = await fetch(
        `${IMPORT_API_BASE}/import-leads/status/${encodeURIComponent(id)}`,
        { headers },
      );

      if (!res.ok) {
        const errorText = await res.text();
        setFeedbackMessage("error", errorText || `Status request failed with status ${res.status}.`);
        return false;
      }

      const data = await res.json();
      applyStatusData(data);
      return true;
    } catch {
      setFeedbackMessage("error", "Status request failed.");
      return false;
    }
  }, [activeRunId, applyStatusData, authToken, setFeedbackMessage]);

  const fetchInboxStatus = useCallback(async (options?: { silent?: boolean }) => {
    if (inboxRequestInFlightRef.current) {
      return;
    }

    if (!authToken) {
      if (!options?.silent) {
        setFeedbackMessage("error", "Auth token is required.");
      }
      return;
    }

    if (!tenantId) {
      if (!options?.silent) {
        setFeedbackMessage("error", "tenant_id is required for inbox checks.");
      }
      return;
    }

    inboxRequestInFlightRef.current = true;
    if (!options?.silent) {
      setIsCheckingInbox(true);
    }

    try {
      const res = await fetch(`${IMPORT_API_BASE}/import-leads/inbox-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ tenant_id: tenantId }),
      });

      const text = await res.text();
      const payload = text ? (JSON.parse(text) as InboxStatusPayload) : {};

      if (!res.ok) {
        if (!options?.silent) {
          setFeedbackMessage("error", payload.message || `Inbox status failed with status ${res.status}.`);
        }
        return;
      }

      applyInboxStatus(payload);
      setInboxWatchTransport("http");
      if (!options?.silent) {
        setFeedbackMessage("success", "Inbox status updated over HTTP.");
      }
    } catch {
      if (!options?.silent) {
        setFeedbackMessage("error", "Inbox status request failed.");
      }
    } finally {
      inboxRequestInFlightRef.current = false;
      setIsCheckingInbox(false);
    }
  }, [applyInboxStatus, authToken, setFeedbackMessage, tenantId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    lastSeenInboxMessageIdRef.current = normalizeMessageId(
      localStorage.getItem(LAST_SEEN_INBOX_MESSAGE_ID_STORAGE_KEY),
    );
  }, []);

  useEffect(() => {
    if (!props.open || typeof window === "undefined") {
      return;
    }

    setRunId("");
    localStorage.removeItem(RUN_ID_STORAGE_KEY);
  }, [props.open]);

  useEffect(() => {
    if (!props.open || !activeRunId || isTerminalRunStatus(statusSummary?.status)) {
      return;
    }

    void fetchStatus(activeRunId);

    const timerId = window.setInterval(() => {
      void fetchStatus(activeRunId);
    }, STATUS_POLL_EVERY_MS);

    return () => window.clearInterval(timerId);
  }, [activeRunId, fetchStatus, props.open, statusSummary?.status]);

  useEffect(() => {
    if (!props.open) {
      disconnectSocket();
      return;
    }

    return () => {
      disconnectSocket();
    };
  }, [disconnectSocket, props.open]);

  useEffect(() => {
    if (!props.open || !liveInboxWatch) {
      return;
    }

    if (canUseWs && !wsConnected && !wsRef.current) {
      connectSocket();
    }

    const runCheck = () => {
      if (document.hidden) {
        return;
      }

      const sentOverSocket = sendInboxStatusOverSocket({ silent: true });
      if (!sentOverSocket) {
        void fetchInboxStatus({ silent: true });
      }
    };

    runCheck();
    const timerId = window.setInterval(runCheck, LIVE_INBOX_POLL_EVERY_MS);

    return () => window.clearInterval(timerId);
  }, [canUseWs, connectSocket, fetchInboxStatus, liveInboxWatch, props.open, sendInboxStatusOverSocket, wsConnected]);

  const handleOnHide = () => {
    const addedCount = normalizeCount(
      pickFirstPopulated(
        statusSummary?.summary?.added,
        statusSummary?.counters?.added,
        importResponse?.summary?.added,
        importSummary?.added,
      ),
    );

    setImporting(false);
    setProgress(0);
    setHasNewMail(false);
    setLiveInboxWatch(false);
    setFeedback(null);
    setImportResponse(null);
    setImportSummary(null);
    setStatusSummary(null);
    disconnectSocket();
    props.onHide({ shouldRefresh: addedCount > 0 });
  };

  const handleImport = async () => {
    if (!tenantId) {
      setFeedbackMessage("error", "No tenant_id found in user profile or auth token.");
      return;
    }

    setRunId("");
    if (typeof window !== "undefined") {
      localStorage.removeItem(RUN_ID_STORAGE_KEY);
    }

    setImporting(true);
    setHasNewMail(false);
    setProgress(0);
    setProgressMode("indeterminate");
    setImportResponse(null);
    setImportSummary(null);
    setStatusSummary(null);
    previousDetectedRef.current = null;
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };
      const res = await fetch(
        `${IMPORT_API_BASE}/import-leads/import`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            tenant_id: tenantId,
          }),
        },
      );

      const data = await res.json();

      if (res.ok && data?.runId) {
        setRunId(data.runId);
        localStorage.setItem(RUN_ID_STORAGE_KEY, data.runId);
        setImportResponse(data);
        setImportSummary(data.summary || null);
        setStatusSummary(null);
        setProgressMode("determinate");
        setFeedbackMessage("success", `Import started with runId ${data.runId}.`);
      } else {
        setImporting(false);
        setProgressMode("determinate");
        setFeedbackMessage("error", `Failed to start import: ${data?.message || "Unknown error"}`);
      }
    } catch (err) {
      setImporting(false);
      setFeedbackMessage("error", `Error starting import: ${String(err)}`);
    }
  };

  const handleCheckInbox = () => {
    const sentOverSocket = sendInboxStatusOverSocket();
    if (!sentOverSocket) {
      void fetchInboxStatus();
    }
  };

  const mergedStatusSummary = {
    ...(importSummary ?? {}),
    ...(isRecord(statusSummary?.summary) ? statusSummary.summary : {}),
    ...(isRecord(statusSummary?.counters) ? statusSummary.counters : {}),
  };
  const statusEntries = Object.entries(mergedStatusSummary).sort(sortMetricEntries);
  const rejectedReasonEntries = toSortedEntries(
    statusSummary?.rejectedReasonCounts ?? importResponse?.rejectedReasonCounts,
  );
  const duplicateReasonEntries = toSortedEntries(
    statusSummary?.duplicateReasonCounts ?? importResponse?.duplicateReasonCounts,
  );
  const rejectedReasonItems = normalizeReasonList(
    statusSummary?.rejectedReasons ?? importResponse?.rejectedReasons,
  );
  const duplicateReasonItems = normalizeReasonList(
    statusSummary?.duplicateReasons ?? importResponse?.duplicateReasons,
  );
  const isRunCompleted = String(statusSummary?.status ?? "").toLowerCase() === "completed";
  const hasRunActivity = Boolean(importResponse || statusSummary || activeRunId);
  const runStatusText = String(
    pickFirstPopulated(statusSummary?.status, importResponse?.status) ?? "",
  ).trim();
  const statusDisplayValue = runStatusText || (importing ? "running" : "ready");
  const runStatusMeta = getRunStatusMeta(statusDisplayValue);
  const progressLabel = !hasRunActivity
    ? "Ready to start"
    : isRunCompleted
      ? "100% complete"
      : progressMode === "determinate"
        ? `${progress}% complete`
        : "Connecting to live status";
  const importButtonSx: SxProps<Theme> = hasNewMail
    ? {
      backgroundColor: "#1c8b6f",
      color: "#fff",
      '&:hover': {
        backgroundColor: "#146a54",
      },
      boxShadow: "0 12px 24px rgba(28, 139, 111, 0.2)",
    }
    : {
      backgroundColor: "#0f8f7a",
      color: "#fff",
      '&:hover': {
        backgroundColor: "#0c7867",
      },
      boxShadow: "0 12px 24px rgba(15, 143, 122, 0.18)",
    };

  const inboxStatusChipColor: "default" | "success" = hasNewMail ? "success" : "default";

  return (
    <Popup
      open={props.open}
      onHide={handleOnHide}
      title="Import Leads"
      fullWidth
      popupBg="#f4f8fb"
      maxWidth="lg"
    >
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          background: "#f7fafc",
        }}
      >
        <Box sx={simpleCardSx}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1.25}
              justifyContent="space-between"
              alignItems={{ lg: "center" }}
            >
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  icon={runStatusMeta.chipIcon}
                  label={formatLabel(statusDisplayValue)}
                  color={runStatusMeta.chipColor}
                  sx={statusChipSx}
                />
                <Chip
                  icon={<LanRoundedIcon fontSize="small" />}
                  label={wsConnected ? "Socket Connected" : "Socket Disconnected"}
                  color={wsConnected ? "success" : "default"}
                  variant={wsConnected ? "filled" : "outlined"}
                  sx={statusChipSx}
                />
                <Chip
                  icon={<MailOutlineRoundedIcon fontSize="small" />}
                  label={hasNewMail ? "New inbox activity" : "Inbox unchanged"}
                  color={inboxStatusChipColor}
                  variant={hasNewMail ? "filled" : "outlined"}
                  sx={statusChipSx}
                />
              </Stack>

              <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                <CustomButton
                  title={importing ? "Starting..." : "Import"}
                  onClick={handleImport}
                  disabled={importing}
                  sx={{
                    ...importButtonSx,
                    minWidth: 120,
                    height: 40,
                    borderRadius: 2.5,
                    px: 2.5,
                  }}
                />
                <CustomButton
                  title={isCheckingInbox ? "Checking..." : "Check Inbox"}
                  onClick={handleCheckInbox}
                  disabled={isCheckingInbox}
                  variant="outlined"
                  sx={simpleSecondaryButtonSx}
                />
                <CustomButton
                  title={wsConnected ? "Disconnect WS" : "Connect WS"}
                  onClick={wsConnected ? disconnectSocket : connectSocket}
                  variant="outlined"
                  sx={simpleSecondaryButtonSx}
                />
              </Stack>
            </Stack>

            <Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                sx={{ mb: 1.25 }}
              >
                <Typography variant="h6" sx={{ color: "#1f2d3d", fontWeight: 700 }}>
                  Import Progress
                </Typography>
                <Typography variant="h6" sx={{ color: runStatusMeta.accent, fontWeight: 700 }}>
                  {progressLabel}
                </Typography>
              </Stack>

              {hasRunActivity ? (
                <LinearProgress
                  variant={isRunCompleted || progressMode === "determinate" ? "determinate" : "indeterminate"}
                  value={isRunCompleted ? 100 : progress}
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    background: runStatusMeta.soft,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      background: runStatusMeta.accent,
                    },
                  }}
                />
              ) : (
                <Box sx={simpleEmptySx}>
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: "#60758b" }} />
                  <Typography variant="body2" sx={{ color: "#60758b" }}>
                    Ready to start import.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={compactSwitchRowSx}>
              <FormControlLabel
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    color: "#1f2d3d",
                    fontWeight: 600,
                  },
                }}
                control={(
                  <Switch
                    checked={liveInboxWatch}
                    onChange={(event) => setLiveInboxWatch(event.target.checked)}
                  />
                )}
                label="Live inbox watch"
              />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Transport ${inboxWatchTransport === "websocket" ? "WebSocket" : "HTTP"}`}
                  sx={metaChipSx}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Folder ${inboxFolder}`}
                  sx={metaChipSx}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Messages ${inboxMessageCount ?? "-"}`}
                  sx={metaChipSx}
                />
              </Stack>
            </Box>

            {feedback ? (
              <Alert severity={feedback.severity} sx={{ borderRadius: 2 }}>
                {feedback.message}
              </Alert>
            ) : null}

            <Divider />

            {statusEntries.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 1.25,
                }}
              >
                {statusEntries.map(([key, value]) => (
                  <Box key={key} sx={getMetricCardSx(key, value)}>
                    <Typography variant="caption" sx={metricLabelSx}>
                      {formatLabel(key)}
                    </Typography>
                    <Typography variant="h6" sx={metricValueSx}>
                      {formatDisplayValue(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}

            {rejectedReasonEntries.length > 0 || rejectedReasonItems.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" sx={simpleSectionHeadingSx}>
                  Rejected
                </Typography>
                <Stack spacing={1.25}>
                  {rejectedReasonEntries.length > 0 ? (
                    <Box sx={reasonGridSx}>
                      {rejectedReasonEntries.map(([key, value]) => (
                        <Box
                          key={key}
                          sx={{
                            ...metricCardBaseSx,
                            borderColor: "#f0ceb4",
                            background: "#fff8f2",
                            minHeight: 0,
                          }}
                        >
                          <Typography variant="caption" sx={metricLabelSx}>
                            {formatLabel(String(key))}
                          </Typography>
                          <Typography variant="h6" sx={metricValueSx}>
                            {formatDisplayValue(value)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : null}

                  {rejectedReasonItems.map((item) => (
                    <Box key={item.key} sx={reasonItemSx("#d97a33", "#fff9f4")}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                        <Typography variant="subtitle2" sx={{ color: "#1f2d3d", fontWeight: 700 }}>
                          {item.primary}
                        </Typography>
                        {item.count !== null && item.count !== undefined ? (
                          <Chip
                            label={`Count: ${item.count}`}
                            size="small"
                            variant="outlined"
                            sx={reasonCountChipSx("#d97a33", "#fff3e8")}
                          />
                        ) : null}
                      </Stack>
                      {item.secondary ? (
                        <Typography variant="body2" sx={{ mt: 1, color: "#60758b" }}>
                          {item.secondary}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : null}

            {duplicateReasonEntries.length > 0 || duplicateReasonItems.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" sx={simpleSectionHeadingSx}>
                  Duplicates
                </Typography>
                <Stack spacing={1.25}>
                  {duplicateReasonEntries.length > 0 ? (
                    <Box sx={reasonGridSx}>
                      {duplicateReasonEntries.map(([key, value]) => (
                        <Box
                          key={key}
                          sx={{
                            ...metricCardBaseSx,
                            borderColor: "#cfe1ef",
                            background: "#f8fbfe",
                            minHeight: 0,
                          }}
                        >
                          <Typography variant="caption" sx={metricLabelSx}>
                            {formatLabel(String(key))}
                          </Typography>
                          <Typography variant="h6" sx={metricValueSx}>
                            {formatDisplayValue(value)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : null}

                  {duplicateReasonItems.map((item) => (
                    <Box key={item.key} sx={reasonItemSx("#1f6fa8", "#f7fbfe")}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                        <Typography variant="subtitle2" sx={{ color: "#1f2d3d", fontWeight: 700 }}>
                          {item.primary}
                        </Typography>
                        {item.count !== null && item.count !== undefined ? (
                          <Chip
                            label={`Count: ${item.count}`}
                            size="small"
                            variant="outlined"
                            sx={reasonCountChipSx("#1f6fa8", "#edf6fc")}
                          />
                        ) : null}
                      </Stack>
                      {item.secondary ? (
                        <Typography variant="body2" sx={{ mt: 1, color: "#60758b" }}>
                          {item.secondary}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </Box>
      </Box>
    </Popup>
  );
};

const simpleCardSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 3,
  border: "1px solid #dce8f2",
  background: "#fff",
  boxShadow: "0 12px 28px rgba(31, 45, 61, 0.06)",
};

const statusChipSx = {
  borderRadius: 999,
  fontWeight: 600,
};

const simpleSecondaryButtonSx = {
  minWidth: 120,
  height: 40,
  px: 2,
  borderRadius: 2.5,
  borderColor: "#c8d7e5",
  color: "#1f2d3d",
  background: "#fff",
  '&:hover': {
    borderColor: "#9ebbd3",
    background: "#f6fafc",
  },
};

const compactSwitchRowSx = {
  p: 1.5,
  borderRadius: 2.5,
  border: "1px solid #e7edf3",
  background: "#fafcfe",
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
};

const metaChipSx = {
  borderColor: "#d2deea",
  color: "#4d657b",
  background: "#fff",
};

const metricCardBaseSx = {
  p: 1.5,
  minHeight: 110,
  borderRadius: 2.5,
  border: "1px solid #edf2f7",
  background: "linear-gradient(180deg, #fbfdff 0%, #f7fbfe 100%)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const reasonGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 1.25,
};

const simpleSectionHeadingSx = {
  mb: 1.25,
  color: "#1f2d3d",
  fontWeight: 700,
};

const metricLabelSx = {
  display: "block",
  color: "#60758b",
  letterSpacing: 0.25,
  mb: 1,
};

const metricValueSx = {
  color: "#1f2d3d",
  fontWeight: 700,
};

const reasonItemSx = (accent: string, background: string): SxProps<Theme> => ({
  p: 1.5,
  borderRadius: 2.5,
  border: "1px solid #e7edf3",
  borderLeft: `4px solid ${accent}`,
  background,
});

const reasonCountChipSx = (accent: string, background: string): SxProps<Theme> => ({
  alignSelf: { xs: "flex-start", sm: "center" },
  borderColor: accent,
  color: accent,
  background,
});

const simpleEmptySx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.25,
  py: 1.1,
  borderRadius: 2,
  border: "1px dashed #d6e2ec",
  background: "#f8fbfe",
};

export default ManageImportLeads;
